import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import PageMeta from '../components/PageMeta';
import RechtsRahmen from '../components/recht/RechtsRahmen';

const STAND = { de: 'Oktober 2026', en: 'October 2026' };

const Impressum = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  
  const isEnglish = i18n.language === 'en' || location.pathname.startsWith('/en');

  return (
    <RechtsRahmen en={isEnglish} titel={isEnglish ? 'Imprint' : 'Impressum'} unterzeile={isEnglish ? 'Provider and contact' : 'Anbieter und Kontakt'}>
      <PageMeta
        title={isEnglish ? 'Imprint | DAVIDMEDIA' : 'Impressum | DAVIDMEDIA'}
        description={
          isEnglish
            ? 'Legal notice and provider identification for davidmedia.de.'
            : 'Anbieterkennzeichnung und Kontaktdaten von DAVIDMEDIA nach § 5 DDG.'
        }
        url={`https://www.davidmedia.de${isEnglish ? '/en/imprint' : '/impressum'}`}
      />


          {/* Content */}
          <motion.div 
            className="space-y-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Card Container */}
            <div className="rounded-2xl p-8 md:p-10 liquid-glass">
              <section className="mb-10">
                <h2 
                  className="font-syne font-bold text-xl mb-4"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {t('impressum.according')}
                </h2>
                <div style={{ color: 'var(--text-secondary)' }}>
                  <p className="mb-1">David Ferfers</p>
                  <p className="mb-1">Bruchhöfe 21</p>
                  <p>47829 Krefeld</p>
                </div>
              </section>

              <section className="mb-10">
                <h2 
                  className="font-syne font-bold text-xl mb-4"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {t('impressum.contact')}
                </h2>
                <div style={{ color: 'var(--text-secondary)' }}>
                  <p className="mb-2">
                    <span style={{ color: 'var(--text-primary)' }}>{isEnglish ? 'Email:' : 'E-Mail:'}</span>{' '}
                    <a 
                      href="mailto:contact@davidmedia.de" 
                      className="hover:text-[#F97316] transition-colors"
                    >
                      contact@davidmedia.de
                    </a>
                  </p>
                  <p>
                    <span style={{ color: 'var(--text-primary)' }}>{isEnglish ? 'Phone:' : 'Telefon:'}</span>{' '}
                    <a 
                      href="tel:+4915174264368" 
                      className="hover:text-[#F97316] transition-colors"
                    >
                      +49 151 74264368
                    </a>
                  </p>
                </div>
              </section>

              <section className="mb-10">
                <h2 
                  className="font-syne font-bold text-xl mb-4"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {isEnglish ? 'VAT ID' : 'Umsatzsteuer-ID'}
                </h2>
                <div style={{ color: 'var(--text-secondary)' }}>
                  <p className="mb-1">
                    {isEnglish ? 'Small business owner according to § 19 UStG' : 'Kleinunternehmer gemäß § 19 UStG'}
                  </p>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                    {isEnglish ? '(No VAT is charged)' : '(Es wird keine Umsatzsteuer ausgewiesen)'}
                  </p>
                </div>
              </section>

              <section className="mb-10">
                <h2
                  className="font-syne font-bold text-xl mb-4"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {isEnglish ? 'Responsible for content' : 'Verantwortlich für den Inhalt'}
                </h2>
                {/* MStV (Medienstaatsvertrag) replaced RStV in November 2020.
                    Old § 55 Abs. 2 RStV → new § 18 Abs. 2 MStV. */}
                <p
                  className="text-sm mb-4"
                  style={{ color: 'var(--text-muted)' }}
                >
                  {isEnglish ? 'According to § 18 (2) MStV' : 'nach § 18 Abs. 2 MStV'}
                </p>
                <div style={{ color: 'var(--text-secondary)' }}>
                  <p className="mb-1">David Ferfers</p>
                  <p className="mb-1">Bruchhöfe 21</p>
                  <p>47829 Krefeld</p>
                </div>
              </section>

              {/* Der Abschnitt „EU-Streitschlichtung“ (Link zur OS-Plattform) ist entfallen: Die EU hat die
                  Plattform am 20.07.2025 eingestellt, die Hinweispflicht aus Art. 14 ODR-Verordnung gibt es nicht mehr. */}

              {/* Consumer dispute statement — required (§ 36 VSBG) so the
                  consumer knows up-front whether you'll go to arbitration. */}
              <section>
                <h2
                  className="font-syne font-bold text-xl mb-4"
                  style={{ color: 'var(--text-primary)' }}
                >
                  {isEnglish ? 'Consumer Dispute Resolution' : 'Verbraucherstreitbeilegung'}
                </h2>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                  {isEnglish
                    ? 'We are not willing or obliged to participate in dispute resolution proceedings before a consumer arbitration board.'
                    : 'Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.'}
                </p>
              </section>
            </div>

            {/* Disclaimer Card */}
            <div className="rounded-2xl p-8 md:p-10 liquid-glass">
              <h2 
                className="font-syne font-bold text-xl mb-6"
                style={{ color: 'var(--text-primary)' }}
              >
                {t('impressum.disclaimer')}
              </h2>
              
              <div className="space-y-6" style={{ color: 'var(--text-secondary)' }}>
                <div>
                  <h3 
                    className="font-semibold mb-2"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {t('impressum.contentLiability')}
                  </h3>
                  <p className="text-sm leading-relaxed">
                    {t('impressum.contentLiabilityText')}
                  </p>
                </div>

                <div>
                  <h3 
                    className="font-semibold mb-2"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {t('impressum.linksLiability')}
                  </h3>
                  <p className="text-sm leading-relaxed">
                    {t('impressum.linksLiabilityText')}
                  </p>
                </div>

                <div>
                  <h3 
                    className="font-semibold mb-2"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {t('impressum.copyright')}
                  </h3>
                  <p className="text-sm leading-relaxed">
                    {t('impressum.copyrightText')}
                  </p>
                </div>
              </div>
            </div>

            {/* Last Updated */}
            <div className="text-center">
              <p 
                className="font-mono text-xs"
                style={{ color: 'var(--text-muted)' }}
              >
                {isEnglish ? 'Last updated: ' : 'Stand: '}
                {isEnglish ? STAND.en : STAND.de}
              </p>
            </div>
          </motion.div>
    </RechtsRahmen>
  );
};

export default Impressum;
